import { useRef, useEffect, useCallback } from 'react'
import { Box, IconButton, Tooltip, Divider } from '@mui/material'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import FormatColorTextIcon from '@mui/icons-material/FormatColorText'

const TOOLS = [
  { cmd: 'bold',                Icon: FormatBoldIcon,           title: 'Negrito (Ctrl+B)'    },
  { cmd: 'italic',              Icon: FormatItalicIcon,         title: 'Itálico (Ctrl+I)'    },
  { cmd: 'underline',           Icon: FormatUnderlinedIcon,     title: 'Sublinhado (Ctrl+U)' },
  { divider: true },
  { cmd: 'insertUnorderedList', Icon: FormatListBulletedIcon,   title: 'Lista'               },
  { cmd: 'insertOrderedList',   Icon: FormatListNumberedIcon,   title: 'Lista numerada'      },
]

const COLORS = [
  { hex: 'inherit', label: 'Padrão'     },
  { hex: '#000000', label: 'Preto'      },
  { hex: '#c62828', label: 'Vermelho'   },
  { hex: '#e65100', label: 'Laranja'    },
  { hex: '#f9a825', label: 'Amarelo'    },
  { hex: '#2e7d32', label: 'Verde'      },
  { hex: '#1565c0', label: 'Azul'       },
  { hex: '#6a1b9a', label: 'Roxo'       },
  { hex: '#757575', label: 'Cinza'      },
]

export default function RichTextEditor({ value, onChange, placeholder = 'Digite a mensagem...' }) {
  const editorRef = useRef(null)

  useEffect(() => {
    if (!editorRef.current) return
    if (editorRef.current !== document.activeElement && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || ''
    }
  }, [value])

  const exec = useCallback((cmd) => {
    editorRef.current?.focus()
    document.execCommand(cmd, false, null)
    onChange(editorRef.current?.innerHTML || '')
  }, [onChange])

  const applyColor = useCallback((hex) => {
    editorRef.current?.focus()
    if (hex === 'inherit') {
      document.execCommand('removeFormat', false, null)
    } else {
      document.execCommand('foreColor', false, hex)
    }
    onChange(editorRef.current?.innerHTML || '')
  }, [onChange])

  const handleInput = useCallback(() => {
    onChange(editorRef.current?.innerHTML || '')
  }, [onChange])

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        '&:focus-within': { borderColor: 'primary.main' },
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 0.25,
          px: 1,
          py: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.subtle',
        }}
      >
        {/* Formatação */}
        {TOOLS.map((t, i) =>
          t.divider ? (
            <Divider key={i} orientation="vertical" flexItem sx={{ mx: 0.5, height: 20, alignSelf: 'center' }} />
          ) : (
            <Tooltip key={t.cmd} title={t.title}>
              <IconButton
                size="small"
                onMouseDown={(e) => { e.preventDefault(); exec(t.cmd) }}
                sx={{ width: 28, height: 28, borderRadius: 1 }}
              >
                <t.Icon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          )
        )}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, height: 20, alignSelf: 'center' }} />

        {/* Cores */}
        <Tooltip title="Cor do texto">
          <FormatColorTextIcon sx={{ fontSize: 15, color: 'text.secondary', mx: 0.25 }} />
        </Tooltip>
        {COLORS.map(({ hex, label }) => (
          <Tooltip key={hex} title={label}>
            <Box
              component="span"
              onMouseDown={(e) => { e.preventDefault(); applyColor(hex) }}
              sx={{
                width: 16,
                height: 16,
                borderRadius: '3px',
                cursor: 'pointer',
                flexShrink: 0,
                bgcolor: hex === 'inherit' ? 'transparent' : hex,
                border: '1.5px solid',
                borderColor: hex === 'inherit' ? 'text.disabled' : hex,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 9,
                color: 'text.disabled',
                '&:hover': { transform: 'scale(1.25)', transition: 'transform 0.1s' },
              }}
            >
              {hex === 'inherit' ? '×' : ''}
            </Box>
          </Tooltip>
        ))}
      </Box>

      {/* Área editável */}
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        data-placeholder={placeholder}
        sx={{
          minHeight: 120,
          px: 1.5,
          py: 1,
          fontSize: 13,
          lineHeight: 1.6,
          outline: 'none',
          color: 'text.primary',
          '&:empty::before': {
            content: 'attr(data-placeholder)',
            color: 'text.disabled',
            pointerEvents: 'none',
          },
          '& ul, & ol': { pl: 3, my: 0.5 },
          '& li': { lineHeight: 1.6 },
        }}
      />
    </Box>
  )
}
